<script lang="ts" setup>
    import { useCompareDeckStore } from "@/store/compareDeck";
    import { computed, ref, inject, onMounted, onUnmounted } from "vue";
    import { storeToRefs } from "pinia";
    import { useSettingsStore } from "@/store/settings";
    import { router } from "@inertiajs/vue3";
    import RothcoCta from "@/components/RothcoCta.vue";
    import CloseIcon from "@icons/closeIcon.svg";
    import TrashIcon from "@icons/trash-icon.svg";
    
    let dialogRef = inject('dialogRef');
    let route:Function = inject("route");

    const store = useCompareDeckStore();

    let {
        allSpecGroups,
        productSpecs,
        productDifferences,
        productSimilarities,
        products,
        colorSwatches,
    } = storeToRefs(store);

    let props = defineProps({
        modal: {
            type: Boolean,
            default: false
        }
    });
    function getMapValues(map: Map<string, any>) {
        return Array.from(map.values());
    }

    function getMapKeys(map: Map<string, any>) {
        return Array.from(map.keys());
    }

    const productCount = computed(() => {
        return productSpecs.value?.size ?? 0;
    });

    function matchesComparison(index, group, value) {
        let productKey = productSpecKeys.value[index];
        let comparison: any = null;
        if (displayType.value == "similarities") {
            comparison = productSimilarities.value;
        } else if (displayType.value == "differences") {
            comparison = productDifferences.value;
        }
        if (comparison) {
            let productInfo = comparison.get(productKey)?.get(group);
            let matchesComparison =
                productInfo?.has(value) ||
                (displayType.value == "differences" && value == "N/A");
            if (matchesComparison) {
                return 'highlighted-item';
            }
        }

        return '';
    }
    const displayType = ref("differences");
    const radioOptions = [
        {
            label: "None",
            value: "none",
            name: "displayType"
        },
        {
            label: "Similarities",
            value: "similarities",
            name: "displayType"
        },
        {
            label: "Differences",
            value: "differences",
            name: "displayType"
        }
    ];

    function removeItem(productName) {
        let product = productSpecs.value.get(productName);
        store.removeProductFromDeck({
            item_index: product.item_index,
            item_variation_id: product.item_variation_id,
            item_name: productName
        });
    }

    let productSpecKeys = computed(() => {
        return Array.from(productSpecs.value.keys());
    });

    let showModal = defineModel();


    let headerRow = ref();

    let fixedHeader = ref(false);

    function getHtml(group, index) {
        let set = productSpecs.value.get(productSpecKeys.value[index]).get(group);
        if(set.size == 0) return "";

        return [...set].join("<br>");
    }

   
    const closeDialog = (data) => {
        dialogRef?.value?.close(data);
    }

    let settings = useSettingsStore()
    let { toggleBuystackFlyout } = settings;
    let { user_id, list, is_dealer} = storeToRefs(settings);

    function addToCart(index) {
        let product = products.value[index];
        if(+product?.pricing?.price_to_use_with_discount > 0){
            let item = {
                source: import.meta.env.VITE_COMPARE_TABLE_SOURCE,
                listId: list.value.list_id,
                current_unit_price: product.pricing.price_to_use_with_discount,
                quantity: 1,
                item_variation_id: product.item_variation_id,
                product_line_code: product.item.product_line_code,
                rothcoItem: product?.rothcoItem,
                item_name: product?.item_name
            }
            closeDialog({ action: 'add_item', item});
        }else{
            settings.setNotificationMessage({
                message: `Could not add ${product?.rothcoItem?.rothco_item_no ?? "item"} to cart.`,
                type: 'error'
            })
            closeDialog({});
        }
    }

    const openEmail = (index)=>{
        let product = products.value[index];
        closeDialog({ action: 'instock_email', variationId: product.item_variation_id});
    }

    let selectedVariationsSorted = computed(() => {
        let variations = new Map();
        let variationGroups = [];
        let variationValues = [];
        for(let product of products.value){
            let specs = product?.variation_specifications ?? [];
            for(let spec of specs){
                let group = spec.group;

                if(variationGroups.findIndex(v => v.spec_group_id == group.spec_group_id) == -1){
                    variationGroups.push(group);
                }

                let value = spec.value;
                if(variationValues.findIndex(v => v.spec_value_id == value.spec_value_id) == -1){
                    variationValues.push(value);
                }
            }
        }

        //sort groups by sort_order(number field)
        variationGroups.sort((a, b) => a.sort_order - b.sort_order);
        
        //sort values by sort_order(number field)
        variationValues.sort((a, b) => a.sort_order - b.sort_order);

        for(let group of variationGroups){
            variations.set(group.spec_group_id, []);
        }

        for(let value of variationValues){
            let values =  variations.get(value.spec_group_id);
            let group = variationGroups.find(v => v.spec_group_id == value.spec_group_id);
            values?.push({
                ...value,
                group
            });
            variations.set(value.spec_group_id, values);

        }
        

        return variations;
        
    });

    function getGroupByGroupId(groupId){
        let group = selectedVariationsSorted.value.get(groupId[0]);
        return group?.[0]?.group.spec_group_name;
    }

    function getSelectedValue(product, variant){
        let specs = product?.variation_specifications ?? [];
        let spec = specs.find(s => s.spec_group_id == variant[0]);
        return spec?.value.spec_value ?? "N/A";
    }

    const getRetailLocatorUrl = (market)=>{
        return route('retail.locator.search', {
            market_type: market,
        });
    }

    const htmlGroups = ['Printing Specs', 'Hazmat Restrictions', 'Care Inst.', 'MSDS', 'Size Chart', 'Links', 'Related Articles', 'Instructions', 'Certification', 'Marketing Material'];

    let removeEventListener = ref(null);

    onMounted(()=>{
        removeEventListener.value = router.on('navigate', ()=>{
             closeDialog({});
        });
    })

    onUnmounted(()=>{
        removeEventListener.value();
    })
</script>
<template>
    <div class="main-section">
        <div @click=closeDialog :class="{
            'modal-overlay': true,
            'active': true
        }"></div>
        <div class="modal" style="max-width: 60rem; height:50rem; display: flex; flex-flow: column;">
            <div class="modal__header" style="border-bottom: none;">
                <h2 id="reviewModalTitle" class="headline-s">Compare Products</h2>
                <CloseIcon @click="closeDialog()" class="modal__header--close" alt="Close Modal" aria-label="Close" />
            </div>
            <div class="filter-options">
                <div class="radio-options flex-row compare-radio-options">
                    <label
                        v-for="{ label, name, value } in radioOptions"
                        :key="`${value}-${name}`"
                    >
                    <input type="radio" v-model="displayType" :value="value" :name="name" aria-label="Select Comparison Type" class="paragraph-details">{{ label }}
                    </label>

                    <!-- <label for="sandbox-mode">
                        <input v-model="form.is_sandbox" :value="true" type="radio" id="sandbox-mode" name="settings-mode" aria-label="Select Sandbox Mode" class="paragraph-details">Sandbox
                        Mode
                    </label>
                    <label for="live-mode">
                        <input  v-model="form.is_sandbox" :value="false" type="radio" id="live-mode" name="settings-mode" aria-label="Select Live Mode" class="paragraph-details">Live Mode
                    </label> -->
                </div>
            
            </div>
            <div style="height: 100%; overflow: auto;">
                <table class="compare-table">
                    <tr>
                        <td v-for="index in productCount" :key="`cell-image-${index}`">
                            <div class="cell-item-product">
                                <img :src="store.getPrimaryImage(productSpecKeys[index - 1])" :alt="productSpecKeys[index-1]" class="cell-item-product__image" />
                                <span class="title-m cell-item-product__title">{{ productSpecKeys[index-1] }}</span>
                                <a @click.prevent="removeItem(productSpecKeys[index - 1])" class="cell-item-product__remove-btn" :aria-label="`Remove Product - ${productSpecKeys[index-1]}`">
                                    <TrashIcon class="remove-icon" alt="remove product" />
                                </a>
                                <div class="cell-item-product__buttons-wrapper">
                                    {{}}
                                    <RothcoCta v-if="products[index-1]?.item?.d2c_view_only == 1 && !is_dealer" 
                                        class="primary small productDetails-order_btn" ctaType="button"
                                        @click="closeDialog()" 
                                        :href="getRetailLocatorUrl(products[index-1]?.item?.market ?? 0)" 
                                        id="find_retailer_button">
                                        Find a Retailer near you
                                    </RothcoCta>
                                    <RothcoCta v-else-if="(products[index-1]?.item?.d2c_view_only == 0 || is_dealer) && products[index-1].in_stock == 1"
                                        @click="addToCart(index-1)" class="secondary small" :aria-label="`Add to cart ${productSpecKeys[index-1]}`">
                                        Add to Cart
                                    </RothcoCta>
                                    <RothcoCta v-else @click="openEmail(index-1)" class="secondary small" :aria-label="`Add to cart ${productSpecKeys[index-1]}`">
                                        {{ `Get Notified When It's ${productSpecKeys[index-1]?.is_active == 1 ? "Back" : "Here"}` }}
                                    </RothcoCta>
                                    <a @click.prevent="toggleBuystackFlyout({seo_name: products[index-1].item.seo_name})">View Details</a>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-for="(variant, index) in selectedVariationsSorted" :key="index">
                        <td v-for="(product, productIndex) in products" :key="productIndex">
                            <div class="cell-item">
                                <label class="paragraph-details cell-item-label">Selected {{ getGroupByGroupId(variant)}}</label>
                                <p :class="['paragraph-details', 'cell-item-value', matchesComparison(productIndex, `Selected ${ getGroupByGroupId(variant)}`, getSelectedValue(product, variant))]">
                                    {{ getSelectedValue(product, variant) }}
                                </p>
                            </div>
                        </td>
                    </tr>
                    <tr v-for="group in allSpecGroups" :key="group">
                        <td
                            v-for="index in productCount"
                            :key="index"
                        >
                        <!-- <div class="cell-item-product"> -->
                            <!-- <span class="title-m cell-item-product__title">Tactical BDU Pants</span>
                            <a class="cell-item-product__remove-btn" aria-label="Remove Product - Tactical BDU Pants">
                                <img src="@icons/trash-icon.svg" class="remove-icon" alt="remove product">
                            </a> -->
                            <!-- <div class="cell-item-product__buttons-wrapper">
                                <button class="secondary small"  aria-label="Add to cart Tactical BDU Pants">
                                    Add to Cart
                                    <span aria-hidden="true" class="icon">
                                        <img src="@icons/carrets-black.svg" alt="arrow right">
                                    </span>
                                </button>
                                <a href="#">View Details</a>
                            </div> -->
                            <div class="cell-item">
                                <label class="paragraph-details cell-item-label">{{ group }}</label>
                                <div class="filter-section color-palette" v-if="group == 'Color'">
                                    <a  v-for="(
                                                value, valueIndex
                                            ) in productSpecs
                                                .get(productSpecKeys[index - 1])
                                                .get(group)" :key="valueIndex" href="#" :class="['variant-item', matchesComparison(index-1, group, value)]" :aria-label="`${color} Color`">
                                                    <img style="width:30px!important;" :src="colorSwatches[value]" :alt="value">{{ value }}</a>

                                </div>
                                <div v-else-if="group.includes('Size') && (group != 'Size Chart' && group != 'Amazon Special Size Type') && productSpecs.get(productSpecKeys[index-1]).get(group).size != 0 " class="filter-section cell-item">
                                    <div class="filter-section variants-palette">
                                        <a style="cursor:default" @click.prevent="preventClick" :class="['variant-item', matchesComparison(index-1, group, value) ]" :aria-label="value" v-for="(
                                                value, valueIndex
                                            ) in productSpecs
                                                .get(productSpecKeys[index - 1])
                                                .get(group)" :key="valueIndex">{{ value }}</a>
                                    </div>
                                </div>
                                <div class="variant-item paragraph-details cell-item-value" v-html="getHtml(group, index-1)" v-else-if="htmlGroups.includes(group)"></div>
                                <p v-else :class="['variant-item paragraph-details cell-item-value', matchesComparison(index-1, group, value) ]" v-for="(
                                                value, valueIndex
                                            ) in productSpecs
                                                .get(productSpecKeys[index - 1])
                                                .get(group)" :key="valueIndex">
                                                {{ value }}
                                                </p>
                                <p class="paragraph-details cell-item-value" v-if="productSpecs.get(productSpecKeys[index-1]).get(group).size == 0">
                                    N/A
                                </p>
                            </div>
                        <!-- </div> -->
                        </td>
                    </tr>
                </table>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
.compare-table {
    & tr:first-of-type {
       position: sticky;
       background-color: white;
       z-index: 1;
       top: 0;
       border-top: none;
    }
    & tr {
       padding: 0.5rem 0.75rem 1.25rem;
       border-top: 0.063rem solid #CCCCCC;
    }
    & td {
        border-top:none;
        height: 100%;
    }

    .cell-item-product__title {
        text-align: center;
        @media screen and (min-width: $breakpoint-tablet) {
            text-align: start;
        }
    }

    .filter-section {
        border-top: none;
    }
}

.highlighted-item {
    background-color: $rothcoBGSolar;

    .variant-item.highlighted-item{
        background-color: unset;
    }
}

::v-deep(button) {
    .icon {
        display: inline !important;
        img {
            width: auto;
        }
    }
}

.cell-item {
    width: 15rem;
    @media screen and (min-width: $breakpoint-tablet) {
        width: 17.5rem;
    }
    padding: 1rem 1.5rem;
    border-top:none;
    // height: 100%
}
.cell-item-product {
    max-width: unset;
    width: 100%;
}
.cell-item-product__image {
    object-fit: contain;
    height: 8rem;
    padding: 0.5rem;
    @media screen and (min-width: $breakpoint-tablet) {
        height: 10rem;
    }
}

.color-palette {
    display:grid;
    justify-items: center;
    align-items: baseline;
    gap: 0.75rem;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: unset;
    // grid-template-rows: repeat(2, 1fr);
    text-align: center;
    .variant-item {
        padding: 0.25rem;
        width: 3.75rem;
        flex: 1 0 21%;
        color: #4D4D4D;
        display: flex;

        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1.6;
        text-decoration: none;
        box-sizing: border-box;
        &.highlighted-item {
            background-color: $rothcoBGSolar;
        }
    }
}

</style>